import { Link, useLocation } from 'react-router-dom';
import { BookOpen, Calendar, Church, Cross, Heart, Menu, X, Moon, Sun } from 'lucide-react';
import { useState } from 'react';
import { LoginArea } from '@/components/auth/LoginArea';
import { getToday } from '@/lib/liturgical/calendar';
import { getColorHex, getNavColorHex } from '@/lib/liturgical/colors';
import { ICEL_COPYRIGHT, USCCB_COPYRIGHT } from '@/lib/liturgical/ordoMissae';
import { useTheme } from '@/hooks/useTheme';
import { Button } from '@/components/ui/button';

interface LayoutProps {
  children: React.ReactNode;
}

const NAV_ITEMS = [
  { path: '/', label: 'Today', icon: Church },
  { path: '/calendar', label: 'Calendar', icon: Calendar },
  { path: '/ordo', label: 'Order of Mass', icon: BookOpen },
  { path: '/readings', label: 'Readings', icon: Cross },
  { path: '/prayers', label: 'Prayers', icon: Heart },
];

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const today = getToday();
  const colorHex = getColorHex(today.color);
  const navColorHex = getNavColorHex(today.color);
  const { theme, setTheme } = useTheme();

  return (
    <div className="min-h-screen flex flex-col bg-stone-50 dark:bg-stone-950">
      {/* Liturgical color accent bar at top */}
      <div className="h-1 w-full shrink-0" style={{ backgroundColor: colorHex }} />

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 sm:px-6">
          {/* Logo — icon image + wordmark */}
          <Link to="/" className="flex items-center gap-2.5 group min-w-0">
            <img
              src="/missal-icon.webp"
              alt="Nostr Missal"
              className="h-9 w-9 shrink-0 rounded-lg object-cover shadow-sm transition-transform duration-200 group-hover:scale-105"
            />
            <div className="min-w-0">
              <span className="block text-[15px] font-semibold tracking-[0.12em] uppercase leading-none text-stone-900 dark:text-stone-100" style={{ fontFamily: '"Cinzel", serif' }}>
                Nostr Missal
              </span>
              <span className="block text-[10px] tracking-widest text-stone-500 dark:text-stone-400 uppercase leading-tight mt-0.5">
                Daily Roman Missal
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-0.5">
            {NAV_ITEMS.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[13px] font-medium tracking-wide transition-all duration-150 ${
                    active
                      ? 'text-white shadow-sm'
                      : 'text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 hover:text-stone-900 dark:hover:text-stone-100'
                  }`}
                  style={active ? { backgroundColor: navColorHex } : undefined}
                >
                  <item.icon className="h-3.5 w-3.5 shrink-0" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 hidden md:flex text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>

            <LoginArea className="hidden md:flex max-w-44" />

            <button
              className="md:hidden rounded-lg p-2 text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-800 hover:text-stone-900 dark:hover:text-stone-100 transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile nav drawer */}
        {menuOpen && (
          <nav className="md:hidden border-t border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 px-4 pb-5 pt-3">
            <div className="flex flex-col gap-1">
              {NAV_ITEMS.map((item) => {
                const active = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMenuOpen(false)}
                    className={`flex items-center gap-3 rounded-lg px-4 py-3 text-[14px] font-medium transition-all ${
                      active
                        ? 'text-white shadow-sm'
                        : 'text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800'
                    }`}
                    style={active ? { backgroundColor: navColorHex } : undefined}
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
            <div className="mt-4 border-t border-stone-200 dark:border-stone-800 pt-4 flex items-center justify-between gap-3">
              <LoginArea className="flex-1" />
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
            </div>
          </nav>
        )}
      </header>

      {/* Main */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="mt-20 border-t border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
        <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
          <div className="mb-6 flex flex-wrap justify-center gap-x-6 gap-y-2">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="text-[12px] tracking-wide uppercase text-stone-400 dark:text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </div>

          <p className="text-center text-amber-700 dark:text-amber-500 text-lg tracking-[0.5em] opacity-60 mb-6 select-none">✦ ✦ ✦</p>

          <div className="space-y-2 text-[11px] leading-relaxed text-stone-400 dark:text-stone-500 text-center">
            <p className="max-w-2xl mx-auto">{ICEL_COPYRIGHT}</p>
            <p className="max-w-2xl mx-auto">{USCCB_COPYRIGHT}</p>
            <div className="flex flex-wrap items-center justify-center gap-3 pt-3 text-xs text-stone-400 dark:text-stone-500">
              <span>
                Vibed with{' '}
                <a href="https://shakespeare.diy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-stone-700 dark:hover:text-stone-300 transition-colors">
                  Shakespeare
                </a>
              </span>
              <span>·</span>
              <span>Built on Nostr</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Bottom accent bar */}
      <div className="h-1 w-full shrink-0" style={{ backgroundColor: colorHex }} />
    </div>
  );
}
